/**
 * Stylized Terrasse Dufferin — boardwalk + pavilion silhouette on the cliff.
 */
import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
} from "three";
import { palette, stageMat, withOutline, type StageMaterial } from "../style/toon";

function mat(color: number) {
  return stageMat(color);
}

const wood = mat(0xc4a574);
const woodDark = mat(0x8a6e4a);
const cream = mat(0xf0e6d4);
const copper = mat(palette.roofCopper);
const rail = mat(palette.stoneDark);

function box(
  w: number,
  h: number,
  d: number,
  material: StageMaterial,
  x = 0,
  y = 0,
  z = 0,
) {
  const m = withOutline(new Mesh(new BoxGeometry(w, h, d), material), 1.03);
  m.position.set(x, y, z);
  return m;
}

export function createTerrasseDufferinModel(): Group {
  const root = new Group();
  root.name = "terrasse-dufferin-hero";
  root.userData = {
    kind: "hero-prop",
    id: "terrasse-dufferin",
    source: "stylized procedural boardwalk",
  };

  const pivot = new Group();
  pivot.name = "pivot_root";
  root.add(pivot);

  // Long boardwalk deck along cliff (local +Z ~ northish)
  pivot.add(box(18, 0.45, 70, wood, 0, 0.3, 0));
  pivot.add(box(18.5, 0.2, 70.5, woodDark, 0, 0.15, 0));

  // Railings
  for (const side of [-8.5, 8.5]) {
    pivot.add(box(0.35, 1.1, 70, rail, side, 1.0, 0));
    for (let i = -30; i <= 30; i += 5) {
      pivot.add(box(0.25, 1.1, 0.25, rail, side, 1.0, i));
    }
  }

  // Central pavilion / kiosk (postcard cue)
  const pavilion = new Group();
  pavilion.name = "pavilion";
  pavilion.position.set(0, 0, 8);
  pavilion.add(box(10, 0.4, 10, wood, 0, 0.5, 0));
  for (const [x, z] of [
    [-3.5, -3.5],
    [3.5, -3.5],
    [-3.5, 3.5],
    [3.5, 3.5],
  ] as const) {
    pavilion.add(
      withOutline(
        new Mesh(new CylinderGeometry(0.35, 0.4, 5.5, 8), cream),
        1.04,
      ),
    );
    pavilion.children[pavilion.children.length - 1].position.set(x, 3.2, z);
  }
  pavilion.add(box(11, 0.5, 11, cream, 0, 6.2, 0));
  const roof = withOutline(
    new Mesh(new ConeGeometry(8.5, 4.5, 8), copper),
    1.05,
  );
  roof.position.y = 9;
  pavilion.add(roof);
  const finial = withOutline(
    new Mesh(new ConeGeometry(0.5, 2.2, 6), copper),
    1.06,
  );
  finial.position.y = 12;
  pavilion.add(finial);
  pivot.add(pavilion);

  // Benches
  for (const z of [-18, -8, 20, 28]) {
    pivot.add(box(3.2, 0.35, 0.7, woodDark, -5, 0.7, z));
    pivot.add(box(3.2, 0.7, 0.15, woodDark, -5, 1.2, z - 0.3));
  }

  const collider = box(20, 8, 72, wood, 0, 4, 0);
  collider.visible = false;
  collider.name = "collider";
  collider.userData.collider = true;
  pivot.add(collider);

  return root;
}
